import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { fetchStatusBarData } from '../api';
import { useLanguage } from '../contexts/LanguageContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { scheduleIdleTask } from '../utils/scheduleIdleTask';
import { publicAsset } from '../utils/publicAsset';

// ===== Helper Function =====
const formatDate = (date: Date, format: string) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return format.replace('{month}', month.toString()).replace('{day}', day.toString());
};

// const parseFlexibleJson = (rawText: string) => {
//   try {
//     if (rawText.startsWith('{')) {
//       // Handle JSON Object (Weather)
//       if (rawText.includes('}{')) {
//         return JSON.parse(rawText.split('}{')[0] + '}');
//       }
//       return JSON.parse(rawText);
//     } else if (rawText.startsWith('[')) {
//       // Handle JSON Array (AQI, Queue)
//       const matches = rawText.match(/\[.*?\]/g);
//       if (!matches) throw new Error('No valid JSON array found');
//       return matches.flatMap(jsonArray => JSON.parse(jsonArray));
//     } else {
//       throw new Error('Unrecognized JSON format');
//     }
//   } catch (err) {
//     console.error('JSON Parsing Error:', err);
//     return null;
//   }
// };

interface StatusBarProps {
  isMobile?: boolean;
}

const StatusBar: React.FC<StatusBarProps> = ({ isMobile = false }) => {
  const { t } = useLanguage();
  const { isDarkMode } = useDarkMode();
  
  const [date, setDate] = useState('');
  const [temperature, setTemperature] = useState<number | null>(null);
  const [weatherIcon, setWeatherIcon] = useState('');
  const [aqiValue, setAqiValue] = useState<number | null>(null);
  const [aqiIcon, setAqiIcon] = useState('');
  const [aqiRating, setAqiRating] = useState('');
  const [queueCount, setQueueCount] = useState<number | null>(null);

  useEffect(() => {
    // AQI 등급을 다국어 텍스트로 변환하는 함수
    const getAqiRatingText = (rating: string) => {
      const ratingMap: { [key: string]: string } = {
        '좋음': t.statusBar.aqiRatings.good,
        '보통': t.statusBar.aqiRatings.normal,
        '나쁨': t.statusBar.aqiRatings.bad,
        '매우나쁨': t.statusBar.aqiRatings.veryBad,
        '데이터없음': t.statusBar.aqiRatings.noData,
      };
      return ratingMap[rating] || rating;
    };

    const loadFromApi = () => {
      fetchStatusBarData()
        .then(data => {
          if (!data) return;

          if (data.date) {
            const dateObj = new Date(data.date);
            if (!isNaN(dateObj.getTime())) {
              setDate(formatDate(dateObj, t.statusBar.dateFormat));
            } else {
              const now = new Date();
              setDate(formatDate(now, t.statusBar.dateFormat));
            }
          } else {
            setDate('');
          }

          setTemperature(data.temperature);

          const skyCode = data.sky_code || 1;
          setWeatherIcon(publicAsset(`/assets/img/sky/sky_0${skyCode}.webp`));

          setAqiValue(data.aqi_value || 0);
          const rawAqiRating = data.aqi_rating || '데이터없음';
          setAqiRating(getAqiRatingText(rawAqiRating));

          const iconMap: { [key: number]: string } = {
            1: 'wt_good.webp',
            2: 'wt_normal.webp',
            3: 'wt_bad.webp',
            4: 'wt_verybad.webp'
          };

          const aqiGrade = data.aqi_grade || 2;
          setAqiIcon(publicAsset(`/assets/img/aqi/${iconMap[aqiGrade]}`));

          setQueueCount(data.queue_count || 0);
        })
        .catch(() => {
          const now = new Date();
          setDate(formatDate(now, t.statusBar.dateFormat));
          setTemperature(null);
          setWeatherIcon(publicAsset('/assets/img/sky/sky_01.webp'));
          setAqiValue(0);
          setAqiRating(t.statusBar.aqiRatings.noData);
          setAqiIcon(publicAsset('/assets/img/aqi/wt_normal.webp'));
          setQueueCount(0);
        });
    };

    const run = () => {
      if (process.env.NODE_ENV === 'development') {
        const now = new Date();
        setDate(formatDate(now, t.statusBar.dateFormat));
        setTemperature(26);
        setWeatherIcon(publicAsset('/assets/img/sky/sky_01.webp'));
        setAqiValue(32);
        setAqiRating(t.statusBar.aqiRatings.normal);
        setAqiIcon(publicAsset('/assets/img/aqi/wt_normal.webp'));
        setQueueCount(5);
        return;
      }
      loadFromApi();
    };

    if (isMobile) {
      scheduleIdleTask(run, 3000);
    } else {
      run();
    }
  }, [t, isMobile]);

  if (isMobile) {
    return (
      <MobileStatusWrapper>
        <CompactWidgetRow>
          <CompactWidget $isDarkMode={isDarkMode}>
            <WeatherContent $isDarkMode={isDarkMode}>
              <img src={weatherIcon} alt="날씨 아이콘" className="weather-icon-compact" />
              <div className="widget-value">{temperature ?? '-'}°C</div>
            </WeatherContent>
            <div className="widget-label">{t.statusBar.sejongWeather}</div>
          </CompactWidget>
          <CompactWidget $isDarkMode={isDarkMode}>
            <AqiContent $isDarkMode={isDarkMode}>
              {aqiIcon && <img src={aqiIcon} alt="AQI" className="aqi-icon-compact" />}
              <div className="widget-value">{aqiValue ?? 0} µg/m³</div>
            </AqiContent>
            <div className="widget-label">{t.statusBar.fineDust}</div>
          </CompactWidget>
        </CompactWidgetRow>
      </MobileStatusWrapper>
    );
  }

  return (
    <StatusBarWrapper>
      <LeftSection>
        <InfoBlock>{date}</InfoBlock>
        <VerticalDivider />
        <InfoBlock>
          {t.statusBar.sejongWeather} {weatherIcon && <IconImage src={weatherIcon} alt="날씨" />} {temperature}°C
        </InfoBlock>
        <VerticalDivider />
        <InfoBlock>
          {t.statusBar.fineDust} {aqiValue} µg/m³ {aqiIcon && <IconImage src={aqiIcon} alt="AQI" />} {aqiRating}
        </InfoBlock>
      </LeftSection>
      <InfoBlock>
        {t.statusBar.passportQueue} <HighlightNumber>{queueCount}</HighlightNumber>{t.statusBar.people}
      </InfoBlock>
    </StatusBarWrapper>
  );

};

export default StatusBar;

// #region ===== Desktop Styled Components =====

const StatusBarWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 5rem 0 5rem;
  background-color: transparent;
  font-size: 1.7rem;
  font-family: 'Noto Sans KR', sans-serif;
  font-weight: 400;
  color: white;
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 150%;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0));
    pointer-events: none;
    z-index: -1;
  }
`;

const HighlightNumber = styled.span`
  font-weight: 700;
  color: #4CAF50;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const InfoBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const VerticalDivider = styled.div`
  width: 1px;
  height: 1.6rem;  // Consistent height for all dividers
  background-color: rgba(255, 255, 255, 0.3);
  margin: 0 1rem;
`;

const IconImage = styled.img`
  width: 24px;
  height: 24px;
`;
// #endregion

//background: white;
const MobileStatusWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const CompactWidgetRow = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const CompactWidget = styled.div<{ $isDarkMode?: boolean }>`
  flex: 1;
  background-color: ${props => props.$isDarkMode ? '#2a2a2a' : '#F0F2F5'};
  border-radius: 12px;
  padding: 1rem 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .widget-label {
    font-size: 1rem;
    color: ${props => props.$isDarkMode ? '#cccccc' : '#6C757D'};
    font-weight: 500;
    text-align: center;
    line-height: 1.2;
  }
`;

const WeatherContent = styled.div<{ $isDarkMode?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .weather-icon-compact {
    width: 20px;
    height: 20px;
  }

  .widget-value {
    font-size: 1.2rem;
    font-weight: 600;
    color: ${props => props.$isDarkMode ? '#ffffff' : '#22468A'};
  }
`;

const AqiContent = styled.div<{ $isDarkMode?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center; 
  gap: 0.25rem;

  .aqi-icon-compact {
    width: 18px;
    height: 18px;
  }

  .widget-value {
    font-size: 1.2rem;
    font-weight: 600;
    color: ${props => props.$isDarkMode ? '#ffffff' : '#22468A'};
  }
`;

// const QueueWidget = styled.div`
//   background: linear-gradient(135deg, #667EEA 0%, #764BA2 100%);
//   border-radius: 16px;
//   padding: 1.5rem 1rem;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
//   transition: transform 0.2s ease, box-shadow 0.2s ease;

//   &:hover {
//     transform: translateY(-3px);
//     box-shadow: 0 8px 30px rgba(102, 126, 234, 0.4);
//   }

//   .queue-content {
//     text-align: center;
//     color: white;
//   }

//   .queue-text {
//     font-size: 1.1rem;
//     font-weight: 500;
//     margin-bottom: 0.5rem;
//     opacity: 0.9;
//   }

//   .queue-number {
//     font-size: 1.8rem;
//     font-weight: 700;
//     display: flex;
//     align-items: baseline;
//     justify-content: center;
//     gap: 0.25rem;

//     .unit {
//       font-size: 1rem;
//       font-weight: 500;
//       opacity: 0.9;
//     }
//   }
// `;
